/**
 * @file Creation de projectiles avec composants Projectile/Visual pooles (reutilises a la destruction : zero allocation en regime).
 */
import { Decimal } from '@shared/bignum';
import type { WeaponBehavior } from '@shared/enums';
import type { EntityId } from '@shared/types';
import type { SimContext } from '../Context';
import type { Projectile, ProjectileKind } from '../ecs/components';
import type { Entity, With } from '../ecs/World';
import { Pool } from '../util/Pool';

const blank = (): Projectile => ({
  kind: 'bullet',
  behavior: 'single',
  ownerId: 0,
  weaponDefId: '',
  slot: 0,
  damage: new Decimal(0),
  crit: false,
  vx: 0,
  vy: 0,
  radius: 0.1,
  rangeLeft: 0,
  lifeLeft: 0,
  pierceLeft: 0,
  hitIds: [],
  explosionRadius: 0,
  arc: null,
  beamEnd: null,
  lockedTargetId: null,
});

const resetProjectile = (p: Projectile): void => {
  p.hitIds.length = 0;
  p.arc = null;
  p.beamEnd = null;
  p.lockedTargetId = null;
  p.crit = false;
};

/** Pool global de composants projectiles (partage entre simulations : le contenu est entierement reinitialise). */
export const projectilePool = new Pool<Projectile>(blank, resetProjectile, 256, 2048);

export interface ProjectileInit {
  kind: ProjectileKind;
  behavior: WeaponBehavior;
  ownerId: EntityId;
  weaponDefId: string;
  slot: number;
  x: number;
  y: number;
  angle: number;
  speed: number;
  radius: number;
  range: number;
  damage: Decimal;
  crit: boolean;
  pierce?: number;
  explosionRadius?: number;
  /** Duree de vie max (s) ; defaut = range / speed (+ marge). */
  life?: number;
  arc?: Projectile['arc'];
  beamEnd?: Projectile['beamEnd'];
  lockedTargetId?: EntityId | null;
}

export const spawnProjectile = (ctx: SimContext, init: ProjectileInit): With<'projectile'> => {
  const e = ctx.rt.world.create('projectile', init.x, init.y);
  const p = projectilePool.acquire();
  p.kind = init.kind;
  p.behavior = init.behavior;
  p.ownerId = init.ownerId;
  p.weaponDefId = init.weaponDefId;
  p.slot = init.slot;
  p.damage = init.damage;
  p.crit = init.crit;
  p.vx = Math.cos(init.angle) * init.speed;
  p.vy = Math.sin(init.angle) * init.speed;
  p.radius = init.radius;
  p.rangeLeft = init.range;
  p.lifeLeft = init.life ?? (init.speed > 0 ? init.range / init.speed + 0.25 : 0.1);
  p.pierceLeft = init.pierce ?? 0;
  p.explosionRadius = init.explosionRadius ?? 0;
  p.arc = init.arc ?? null;
  p.beamEnd = init.beamEnd ?? null;
  p.lockedTargetId = init.lockedTargetId ?? null;
  e.projectile = p;
  e.transform.rot = init.angle;
  e.visual = { defId: init.behavior, scale: 1, hitFlash: 0, dying: 0 };
  return e as With<'projectile'>;
};

/** A brancher sur World.onDestroy : rend le composant au pool. */
export const releaseProjectile = (e: Entity): void => {
  if (e.projectile) {
    projectilePool.release(e.projectile);
    e.projectile = undefined;
  }
};
